import { Clear } from '@mui/icons-material';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { capitalize } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { ContainerWithVerticalScrollShadow, FormField } from '../../../components/Form/FormComponents';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { SortableList } from '../../../components/SortableList/SortableList';
import { MprItemPicker } from './MprItemPicker';
import { MprPanelHeading, MprEmptyListText } from './MprPanelHeading';
import { mprListContainerCss, mprListRowCss } from './mprSettingsStyles';
import {
  getMprItemKey,
  isMprKpiItem,
  isMprMetricsItem,
  MprItem,
  uniqueMprItems,
  useMprItemDisplayName,
} from './multiPeriodReportingUtils';
import { useMprColumnConfigs } from './useMprColumnConfigs';

type ItemWithId = MprItem & { id: string };

const ItemPickerLabel = 'Pick KPIs, metrics and custom fields';

export function MprItemsPanel() {
  const { draft, setDraft } = useMprColumnConfigs();
  const itemDisplayName = useMprItemDisplayName();

  const items: ItemWithId[] = useMemo(
    () => draft.items.map((item) => ({ ...item, id: getMprItemKey(item) })),
    [draft.items]
  );
  const onReorder = useCallback(
    (reordered: ItemWithId[]) => {
      setDraft((prev) => ({ ...prev, items: reordered.map(({ id, ...item }) => item as MprItem) }));
    },
    [setDraft]
  );
  const onRemove = useCallback(
    (id: string) => {
      setDraft((prev) => ({ ...prev, items: prev.items.filter((item) => getMprItemKey(item) !== id) }));
    },
    [setDraft]
  );
  const onItemsChange = useCallback(
    (next: MprItem[]) => {
      setDraft((prev) => ({ ...prev, items: uniqueMprItems(next) }));
    },
    [setDraft]
  );

  return (
    <Stack gap='0.5rem' minWidth={0}>
      <MprPanelHeading title='What' hint='KPIs, metrics and custom fields, in column order' />
      <FormField>
        <MprItemPicker label={ItemPickerLabel} selected={draft.items} onChange={onItemsChange} />
      </FormField>
      {items.length === 0 ? (
        <MprEmptyListText css={mprListContainerCss}>
          Nothing to report yet. Pick a KPI, metric or custom field.
        </MprEmptyListText>
      ) : (
        <ContainerWithVerticalScrollShadow css={mprListContainerCss} role='group' aria-label='Items'>
          <SortableList
            items={items}
            onChange={onReorder}
            renderItem={(item) => (
              <SortableItem id={item.id}>
                <ItemRow item={item} name={itemDisplayName(item)} onRemove={onRemove} />
              </SortableItem>
            )}
          />
        </ContainerWithVerticalScrollShadow>
      )}
    </Stack>
  );
}

const ItemTypeLabel = { kpi: 'KPI', metric: 'Metric', customField: 'Custom field' } as const;
function itemType(item: MprItem): keyof typeof ItemTypeLabel {
  return isMprKpiItem(item) ? 'kpi' : isMprMetricsItem(item) ? 'metric' : 'customField';
}

function ItemRow({
  item,
  name,
  onRemove,
}: {
  item: ItemWithId;
  name: string;
  onRemove: (id: string) => void;
}) {
  return (
    <Typography component='div' variant='body2' css={mprListRowCss}>
      <BasicDragHandle />
      <div>
        {name}
        {isMprKpiItem(item) && (
          <Typography component='span' variant='body2' color='text.secondary'>
            {` · ${capitalize(item.section)}`}
          </Typography>
        )}
      </div>
      <Chip size='small' label={ItemTypeLabel[itemType(item)]} />
      <IconButton aria-label={`Remove ${name}`} onClick={() => onRemove(item.id)}>
        <Clear fontSize='small' />
      </IconButton>
    </Typography>
  );
}
